import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FocusTrap } from "focus-trap-react";

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    ariaLabelledby?: string;
    className?: string;
}

const Modal: React.FC<ModalProps> = ({ className = ``, isOpen, onClose, children, ariaLabelledby }) => {
    const previousActiveElement = useRef<HTMLElement | null>(null);

    useEffect((): void | (() => void) => {
        if (!isOpen) return;

        previousActiveElement.current = document.activeElement as HTMLElement;
        document.body.style.overflow = `hidden`;

        const main = document.querySelector(`main`);
        if (main) main.setAttribute(`inert`, ``);

        const pageHeader = document.querySelector(`#page-header`);
        const pageFooter = document.querySelector(`#page-footer`);
        if (pageHeader) pageHeader.setAttribute(`inert`, ``);
        if (pageFooter) pageFooter.setAttribute(`inert`, ``);

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === `Escape`) onClose();
        };
        document.addEventListener(`keydown`, handleEscape);

        return () => {
            document.body.style.overflow = ``;
            const main = document.querySelector(`main`);
            if (main) main.removeAttribute(`inert`);
            const pageHeader = document.querySelector(`#page-header`);
            const pageFooter = document.querySelector(`#page-footer`);
            if (pageHeader) pageHeader.removeAttribute(`inert`);
            if (pageFooter) pageFooter.removeAttribute(`inert`);
            document.removeEventListener(`keydown`, handleEscape);
            previousActiveElement.current?.focus();
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <FocusTrap>
            <div>
                {/* Backdrop */}
                <div
                    className="fixed inset-0 z-40 bg-black bg-opacity-50 backdrop-blur-sm"
                    onClick={onClose}
                />
                {/* Dialog */}
                <div className={`fixed inset-0 z-50 flex items-center justify-center pointer-events-none ${className}`}>
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={ariaLabelledby}
                        className="pointer-events-auto max-w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {children}
                    </div>
                </div>
            </div>
        </FocusTrap>,
        document.body
    );
};

export default Modal;
